import {
  TableContainer,
  Table,
  TableCaption,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Button,
  Flex,
  Tfoot,
  Spacer,
  Image,
  Heading,
} from "@chakra-ui/react";
import { useGameContext } from "../../../context/gameContext/gameContext";
import { useNavigate } from "react-router-dom";

import { useAppContext } from "../../../context/appContext/appContext";

const RoundSummary = ({
  gameOver,
  continueToNextRound,
  setIsGameOver,
  playSameSettings,
}) => {
  // gameOver prop is added to a call if game is over, include another line "player x won the game" - confetti
  const {
    points,
    teamAScore,
    teamBScore,
    winningBid,
    winningScore,
    trump,
    resetGameSettings,
    gameMode,
    numPlayers,
    username,
    botNames,
    individualScores,
    individualPoints,
  } = useGameContext();

  const isCutthroat = gameMode === "Cutthroat";

  const { notGS } = useAppContext();

  const navigate = useNavigate();

  const { bid, team, success } = winningBid;

  // 2v2 Teams tie-breaker: if both teams ended the deciding hand at/above
  // the winning score, the team that bid that hand wins outright rather
  // than whichever raw score happens to be higher - mirrors
  // Table.js's teamAWinsGame exactly (duplicated here since this is a
  // separate component reading the same context fields).
  const teamAWinsTheGame =
    teamAScore >= winningScore && teamBScore >= winningScore
      ? team === "teamA"
      : teamAScore > teamBScore;

  const teamFormatted = team === "teamA" ? "We" : "They";
  const { game, high, jack, low, us, them, usFinal, themFinal } = points;

  const usPoint = us === 1 ? "garbage point" : "garbage points";
  const themPoint = them === 1 ? "garbage point" : "garbage points";

  const usSet = `We are set ${bid}. They made ${them} ${themPoint}.`;
  const themSet = `They are set ${bid}. We made ${us} ${usPoint}.`;
  const usSuccess = `We made our ${bid} bid and earned a total of ${usFinal} points. They made ${them} ${themPoint}.`;
  const themSuccess = `They made their ${bid} bid and earned a total of ${themFinal} points. We made ${us} ${usPoint}.`;

  let tableCaption;
  if (teamFormatted === "We" && success) {
    tableCaption = usSuccess;
  } else if (teamFormatted === "We" && !success) {
    tableCaption = usSet;
  } else if (teamFormatted === "They" && success) {
    tableCaption = themSuccess;
  } else tableCaption = themSet;

  const FcOkay = (
    <Image src="/CheckMark_Icon.svg" width={{ base: "14px", md: "20px" }} />
  );
  const FcHPriority = (
    <Image src="/XMark_Icon.svg" width={{ base: "14px", md: "20px" }} />
  );

  const highCard = high.card;
  const lowCard = low.card;
  const theJack = jack.card;

  function IconWithCard(icon, card) {
    return (
      <Flex align="center" gap="5px">
        {icon}
        {card}
      </Flex>
    );
  }

  const usHigh =
    high.team === "Us" ? IconWithCard(FcOkay, highCard) : FcHPriority;
  const themHigh =
    high.team === "Them" ? IconWithCard(FcOkay, highCard) : FcHPriority;

  const usLow = low.team === "Us" ? IconWithCard(FcOkay, lowCard) : FcHPriority;
  const themLow =
    low.team === "Them" ? IconWithCard(FcOkay, lowCard) : FcHPriority;

  const usGame =
    game.team === "Us"
      ? IconWithCard(FcOkay, game.teamA)
      : IconWithCard(FcHPriority, game.teamA);
  const themGame =
    game.team === "Them"
      ? IconWithCard(FcOkay, game.teamB)
      : IconWithCard(FcHPriority, game.teamB);

  const isJack = jack.team === null;

  const usJack = isJack
    ? "N/A"
    : jack.team === "Us"
    ? IconWithCard(FcOkay, theJack)
    : FcHPriority;
  const themJack = isJack
    ? "N/A"
    : jack.team === "Them"
    ? IconWithCard(FcOkay, theJack)
    : FcHPriority;

  const resetGame = () => {
    notGS();
    resetGameSettings();
    setIsGameOver(false);
    navigate("/play");
  };

  const gameStatus = gameOver ? (
    <Flex gap="30px">
      <Button onClick={playSameSettings}>Play Again</Button>
      <Button onClick={resetGame}>Game Menu</Button>
    </Flex>
  ) : (
    <Button onClick={continueToNextRound}>Continue</Button>
  );

  // Cutthroat: same layout, but a dynamic column per active seat (3 or 4)
  // named for whoever's actually playing, instead of the fixed Us/Them
  // pair - kept as a fully separate render path rather than reworking the
  // Us/Them JSX below, which stays exactly as it was for 2v2 Teams.
  if (isCutthroat) {
    const activeSeats = Array.from(
      { length: Number(numPlayers) },
      (_, i) => `P${i + 1}`
    );
    const nameOf = (seat) => (seat === "P1" ? username : botNames[seat]);
    const { bid, player: bidderSeat, success } = winningBid;
    const bidderName = bidderSeat ? nameOf(bidderSeat) : "";
    const {
      high: cHigh,
      low: cLow,
      jack: cJack,
      game: cGame,
      pointsWon,
      roundScores,
      winners: gameWinners = [],
    } = individualPoints;
    const isJackNA = cJack.player === null;

    // Cutthroat can end in co-winners (see checkGameOverIndividual in
    // Table.js) - join their names with standard list grammar ("A won!!!",
    // "A and B won!!!", "A, B, and C won!!!"). "won" (not "wins") reads
    // correctly for both a singular and a plural subject in English past
    // tense, so no separate pluralization is needed.
    const winnerNames = gameWinners.map(nameOf);
    const winnerText =
      winnerNames.length <= 1
        ? winnerNames[0] || ""
        : winnerNames.length === 2
        ? `${winnerNames[0]} and ${winnerNames[1]}`
        : `${winnerNames.slice(0, -1).join(", ")}, and ${
            winnerNames[winnerNames.length - 1]
          }`;

    const cutthroatCaption = !bidderSeat
      ? ""
      : success
      ? `${bidderName} made their ${bid} bid and earned a total of ${roundScores[bidderSeat]} points.`
      : `${bidderName} is set ${bid}.`;

    return (
      <Flex
        direction={"column"}
        align="center"
        bg="white"
        gap={{ base: "4px", md: "10px" }}
        borderRadius={"8px"}
        fontFamily="Shantell Sans"
        position="relative"
        zIndex={3}
        p={{ base: "6px", md: "15px" }}
        maxW={{ base: "80vw", md: "95vw" }}
        w="max-content"
      >
        <Heading size={{ base: "sm", md: "lg" }} fontFamily="Shantell Sans">
          {gameOver ? "Game Over" : "Round Summary"}
        </Heading>
        {gameOver ? (
          <Heading size={{ base: "xs", md: "md" }} fontFamily="Shantell Sans">
            {`${winnerText} won!!!`}
          </Heading>
        ) : (
          <Heading
            size={{ base: "xs", md: "sm" }}
            fontFamily="Shantell Sans"
            fontStyle={"italic"}
          >
            {bidderSeat ? `${bidderName} bid ${bid} in ${trump}` : ""}
          </Heading>
        )}

        <TableContainer bg="white" mt={{ base: "6px", md: "15px" }}>
          <Table variant="simple" size="sm">
            <TableCaption
              fontFamily="Shantell Sans"
              fontSize={{ base: "10px", md: "16px" }}
              width="min(75vw, 400px)"
              whiteSpace="normal"
            >
              {cutthroatCaption}
            </TableCaption>
            <Thead>
              <Tr>
                <Th
                  fontFamily="Shantell Sans"
                  fontSize={{ base: "10px", md: "15px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Points
                </Th>
                {activeSeats.map((seat) => (
                  <Th
                    key={seat}
                    fontFamily="Shantell Sans"
                    fontSize={{ base: "10px", md: "15px" }}
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {nameOf(seat)}
                  </Th>
                ))}
              </Tr>
            </Thead>

            <Tbody>
              <Tr>
                <Td
                  fontSize={{ base: "10px", md: "14px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  High
                </Td>
                {activeSeats.map((seat) => (
                  <Td
                    key={seat}
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {cHigh.player === seat
                      ? IconWithCard(FcOkay, cHigh.card)
                      : FcHPriority}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td
                  fontSize={{ base: "10px", md: "14px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Low
                </Td>
                {activeSeats.map((seat) => (
                  <Td
                    key={seat}
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {cLow.player === seat
                      ? IconWithCard(FcOkay, cLow.card)
                      : FcHPriority}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td
                  fontSize={{ base: "10px", md: "14px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Jack
                </Td>
                {activeSeats.map((seat) => (
                  <Td
                    key={seat}
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {isJackNA
                      ? "N/A"
                      : cJack.player === seat
                      ? IconWithCard(FcOkay, cJack.card)
                      : FcHPriority}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td
                  fontSize={{ base: "10px", md: "14px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Game
                </Td>
                {activeSeats.map((seat) => (
                  <Td
                    key={seat}
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {cGame.player === seat
                      ? IconWithCard(FcOkay, cGame.scores[seat])
                      : IconWithCard(FcHPriority, cGame.scores[seat])}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td
                  height={{ base: "10px", md: "25px" }}
                  colSpan={activeSeats.length + 1}
                ></Td>
              </Tr>
            </Tbody>

            <Tfoot>
              <Tr>
                <Th
                  fontFamily="Shantell Sans"
                  fontSize={{ base: "9px", md: "14px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Points Won
                </Th>
                {activeSeats.map((seat) => (
                  <Th
                    key={seat}
                    fontFamily="Shantell Sans"
                    fontSize={{ base: "12px", md: "18px" }}
                    fontWeight="normal"
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {pointsWon[seat]}
                  </Th>
                ))}
              </Tr>
              <Tr>
                <Th
                  fontFamily="Shantell Sans"
                  fontSize={{ base: "9px", md: "14px" }}
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Points Needed
                </Th>
                {activeSeats.map((seat) => (
                  <Th
                    key={seat}
                    fontFamily="Shantell Sans"
                    fontSize={{ base: "12px", md: "18px" }}
                    fontWeight="normal"
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {seat === bidderSeat ? bid : 0}
                  </Th>
                ))}
              </Tr>

              <Tr>
                <Th
                  fontFamily="Shantell Sans"
                  fontSize={{ base: "9px", md: "14px" }}
                  fontWeight="bold"
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  Adjusted Points
                </Th>
                {activeSeats.map((seat) => (
                  <Th
                    key={seat}
                    fontFamily="Shantell Sans"
                    fontSize={{ base: "12px", md: "18px" }}
                    fontWeight="normal"
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    {roundScores[seat]}
                  </Th>
                ))}
              </Tr>
              <Tr>
                <Td
                  height={{ base: "10px", md: "25px" }}
                  colSpan={activeSeats.length + 1}
                ></Td>
              </Tr>

              <Tr>
                <Th
                  fontFamily="Shantell Sans"
                  fontSize={{ base: "9px", md: "14px" }}
                  fontWeight="bold"
                  px={{ base: "4px", md: "16px" }}
                  py={{ base: "2px", md: "10px" }}
                >
                  {gameOver ? "Final Score" : "Updated Score"}
                </Th>
                {activeSeats.map((seat) => (
                  <Th
                    key={seat}
                    fontFamily="Shantell Sans"
                    fontSize={{ base: "13px", md: "20px" }}
                    px={{ base: "4px", md: "16px" }}
                    py={{ base: "2px", md: "10px" }}
                  >
                    <span
                      className={
                        gameOver && gameWinners.includes(seat) ? "circle" : ""
                      }
                    >
                      {individualScores[seat]}
                    </span>
                  </Th>
                ))}
              </Tr>
            </Tfoot>
          </Table>
        </TableContainer>
        <Spacer />
        {gameStatus}
      </Flex>
    );
  }

  return (
    <Flex
      direction={"column"}
      align="center"
      bg="white"
      gap={{ base: "4px", md: "10px" }}
      borderRadius={"8px"}
      fontFamily="Shantell Sans"
      position="relative"
      zIndex={3}
      p={{ base: "6px", md: "15px" }}
      maxW={{ base: "80vw", md: "95vw" }}
      w="max-content"
    >
      <Heading size={{ base: "sm", md: "lg" }} fontFamily="Shantell Sans">
        {gameOver ? "Game Over" : "Round Summary"}
      </Heading>
      {gameOver ? (
        <Heading size={{ base: "xs", md: "md" }} fontFamily="Shantell Sans">
          {teamAWinsTheGame ? "We won!!!" : "They won.."}
        </Heading>
      ) : (
        <Heading
          size={{ base: "xs", md: "sm" }}
          fontFamily="Shantell Sans"
          fontStyle={"italic"}
        >
          {`${teamFormatted} bid ${bid} in ${trump}`}
        </Heading>
      )}

      <TableContainer bg="white" mt={{ base: "6px", md: "15px" }}>
        <Table variant="simple" size="sm">
          <TableCaption
            fontFamily="Shantell Sans"
            fontSize={{ base: "10px", md: "16px" }}
            width="min(75vw, 400px)"
            whiteSpace="normal"
          >
            {tableCaption}
          </TableCaption>
          <Thead>
            <Tr>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "10px", md: "15px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Points
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "10px", md: "15px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Us
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "10px", md: "15px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Them
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            <Tr>
              <Td
                fontSize={{ base: "10px", md: "14px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                High
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {usHigh}
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {themHigh}
              </Td>
            </Tr>
            <Tr>
              <Td
                fontSize={{ base: "10px", md: "14px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Low
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {usLow}
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {themLow}
              </Td>
            </Tr>
            <Tr>
              <Td
                fontSize={{ base: "10px", md: "14px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Jack
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {usJack}
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {themJack}
              </Td>
            </Tr>
            <Tr>
              <Td
                fontSize={{ base: "10px", md: "14px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Game
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {usGame}
              </Td>
              <Td px={{ base: "4px", md: "16px" }} py={{ base: "2px", md: "10px" }}>
                {themGame}
              </Td>
            </Tr>
            <Tr>
              <Td height={{ base: "10px", md: "25px" }} colSpan="3"></Td>
            </Tr>
          </Tbody>

          <Tfoot>
            <Tr>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "9px", md: "14px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Points Won
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "12px", md: "18px" }}
                fontWeight="normal"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {us}
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "12px", md: "18px" }}
                fontWeight="normal"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {them}
              </Th>
            </Tr>
            <Tr>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "9px", md: "14px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Points Needed
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "12px", md: "18px" }}
                fontWeight="normal"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {teamFormatted === "We" ? bid : 0}
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "12px", md: "18px" }}
                fontWeight="normal"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {teamFormatted === "They" ? bid : 0}
              </Th>
            </Tr>

            <Tr>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "9px", md: "14px" }}
                fontWeight="bold"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                Adjusted Points
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "12px", md: "18px" }}
                fontWeight="normal"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {usFinal}
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "12px", md: "18px" }}
                fontWeight="normal"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {themFinal}
              </Th>
            </Tr>
            <Tr>
              <Td height={{ base: "10px", md: "25px" }} colSpan="3"></Td>
            </Tr>

            <Tr>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "9px", md: "14px" }}
                fontWeight="bold"
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                {gameOver ? "Final Score" : "Updated Score"}
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "13px", md: "20px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                <span
                  className={teamAWinsTheGame && gameOver ? "circle" : ""}
                >
                  {teamAScore}
                </span>
              </Th>
              <Th
                fontFamily="Shantell Sans"
                fontSize={{ base: "13px", md: "20px" }}
                px={{ base: "4px", md: "16px" }}
                py={{ base: "2px", md: "10px" }}
              >
                <span
                  className={!teamAWinsTheGame && gameOver ? "circle" : ""}
                >
                  {teamBScore}
                </span>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
      <Spacer />
      {gameStatus}
    </Flex>
  );
};
export default RoundSummary;
